import React from 'react';
import { useLanguage } from '../../LanguageContext';
import { getTranslation } from '../../translations';
import { 
    Search, Plus, Minus, Package, Edit2, Trash2, AlertCircle, 
    Layers, X, DollarSign, TrendingUp, TrendingDown, ArrowDownLeft 
} from 'lucide-react';
import { formatNumber, formatCurrency } from '../../utils/formatNumber';

const Card = ({ children, className = "", noPadding = false }) => (
  <div className={`bg-white rounded-2xl sm:rounded-3xl border border-slate-100 shadow-[0_2px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.08)] transition-all duration-300 ${className}`}>
    {noPadding ? children : <div className="p-4 sm:p-6 md:p-8">{children}</div>}
  </div>
);

const Badge = ({ children, color = "slate", className = "" }) => {
  const styles = {
    slate: "bg-slate-100 text-slate-600 border-slate-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-100 ring-1 ring-emerald-500/20",
    red: "bg-rose-50 text-rose-700 border-rose-100 ring-1 ring-rose-500/20",
    blue: "bg-blue-50 text-blue-700 border-blue-100 ring-1 ring-blue-500/20",
    amber: "bg-amber-50 text-amber-700 border-amber-100 ring-1 ring-amber-500/20",
    purple: "bg-purple-50 text-purple-700 border-purple-100 ring-1 ring-purple-500/20",
    orange: "bg-orange-50 text-orange-700 border-orange-100 ring-1 ring-orange-500/20",
  };
  return (
    <span className={`px-3 py-1 rounded-full text-[11px] font-semibold border ${styles[color] || styles.slate} ${className}`}>
      {children}
    </span>
  );
};

const Button = ({ children, onClick, variant = "primary", className = "", disabled = false, icon: Icon, fullWidth = false, ...props }) => {
  const variants = {
    primary: "bg-[#10056E] hover:bg-[#0d0459] text-white shadow-lg shadow-[#10056E]/30",
    secondary: "bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 shadow-sm",
    danger: "bg-rose-500 hover:bg-rose-600 text-white shadow-lg shadow-rose-500/30",
    ghost: "bg-transparent hover:bg-slate-100 text-slate-600",
  };
  
  return (
    <button 
      onClick={onClick} 
      disabled={disabled}
      className={`
        inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl text-sm font-semibold transition-all duration-200 active:scale-[0.98] touch-manipulation
        disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100
        ${variants[variant]} 
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      {...props}
    >
      {Icon && <Icon size={18} className={disabled ? 'animate-spin' : ''} />}
      {children}
    </button>
  );
};

/**
 * SKU Management Component
 * Handles product/SKU listing, search, and management in Settings
 */
const SKUManagement = React.memo(({

    searchTerm,
    setSearchTerm,
    filteredInventory,
    priceLogs,
    can,
    setIsCategoryModalOpen,
    setSelectedItem,
    setIsItemModalOpen,
    setViewingImage,
    setSelectedItemForPriceLog,
    setShowPriceLogModal,
    requestConfirm,
    db,
    fetchData
}) => {
    const { language } = useLanguage();
    const t = getTranslation(language);
    return (
        <div className="space-y-4 sm:space-y-6">
            {/* Search and Actions Bar */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input 
                        className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:border-[#10056E] focus:ring-2 focus:ring-[#10056E]/20 outline-none transition-all text-sm font-medium text-slate-700" 
                        placeholder={t("settings.searchProductPlaceholder")} 
                        value={searchTerm} 
                        onChange={e => setSearchTerm(e.target.value)} 
                    />
                </div>
                <div className="flex gap-2">
                    <Button variant="secondary" onClick={() => setIsCategoryModalOpen(true)} icon={Layers} className="flex-1 sm:flex-initial justify-center text-xs sm:text-sm">{t("settings.categoryBtn")}</Button>
                    <Button onClick={() => { setSelectedItem(null); setIsItemModalOpen(true); }} icon={Plus} className="flex-1 sm:flex-initial justify-center text-xs sm:text-sm">{t("settings.newProduct")}</Button>
                </div>
            </div>
            
            {/* SKU Table */}
            <Card noPadding>
                {/* Desktop Table */}
                <div className="overflow-x-auto hidden md:block">
                    <table className="w-full text-sm">
                        <thead className="bg-gradient-to-r from-slate-50 to-slate-100/50 text-slate-600 font-semibold text-xs uppercase tracking-wider">
                            <tr>
                                <th className="p-4 text-left">{t("settings.productCol")}</th>
                                <th className="p-4 text-left">{t("settings.categoryCol")}</th>
                                {can.viewFinancials && <th className="p-4 text-right">{t("settings.priceCol")}</th>}
                                <th className="p-4 text-center">{t("settings.stockCol")}</th>
                                <th className="p-4 text-center">{t("settings.burstLimitMonthly")}</th>
                                <th className="p-4 text-center">{t("settings.actionsCol")}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                            {filteredInventory.map(item => {
                                // Get latest price change for this item
                                const itemPriceLogs = priceLogs.filter(log => log.itemId === item.id);
                                const latestPriceLog = itemPriceLogs.length > 0 ? itemPriceLogs[0] : null;
                                const latestMeta = latestPriceLog?.metadata;
                                const hasPriceChange = latestMeta && latestMeta.newPrice !== latestMeta.oldPrice;
                                const isPriceIncrease = hasPriceChange && latestMeta.newPrice > latestMeta.oldPrice;
                                
                                return (
                                <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="p-4">
                                        <div className="flex items-center gap-3">
                                            <div 
                                                className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center overflow-hidden border border-slate-200 shadow-sm cursor-pointer hover:ring-2 hover:ring-[#10056E] transition-all"
                                                onClick={() => item.image && setViewingImage(item.image)}
                                                title={item.image ? t("inventory.viewFullImage") : ""}
                                            >
                                                {item.image ? <img src={item.image} className="w-full h-full object-cover" alt=""/> : <Package size={20} className="text-slate-400"/>}
                                            </div>
                                            <div>
                                                <div className="font-bold text-slate-800">{item.name}</div>
                                                <div className="text-xs text-slate-400 font-mono mt-0.5">{item.sku}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <Badge color="blue">{item.category}</Badge>
                                    </td>
                                    {can.viewFinancials && (
                                        <td className="p-4 text-right">
                                            <div className="flex items-center justify-end gap-1.5">
                                                <span className="font-bold text-slate-800">{formatCurrency(item.price)}</span>
                                                {hasPriceChange && (
                                                    <div className={`flex items-center justify-center w-5 h-5 rounded ${
                                                        isPriceIncrease ? 'bg-emerald-100' : 'bg-rose-100'
                                                    }`} title={isPriceIncrease ? t('settings.priceUp') : t('settings.priceDown')}>
                                                        {isPriceIncrease ? (
                                                            <TrendingUp size={12} className="text-emerald-600" />
                                                        ) : (
                                                            <TrendingDown size={12} className="text-rose-600" />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                    <td className="p-4 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            <span className={`px-3 py-1.5 rounded-lg text-sm font-bold ${
                                                item.quantity <= item.minLevel 
                                                    ? 'bg-rose-100 text-rose-700 ring-2 ring-rose-200' 
                                                    : 'bg-emerald-100 text-emerald-700'
                                            }`}>
                                                {formatNumber(item.quantity)} {item.unit || 'pcs'}
                                            </span>
                                            {item.quantity <= item.minLevel && (
                                                <AlertCircle size={16} className="text-rose-500" title={t("settings.lowStockTooltip")} />
                                            )}
                                        </div>
                                    </td>
                                    <td className="p-4 text-center">
                                        <span className="px-3 py-1.5 rounded-lg text-sm font-bold bg-rose-100 text-rose-700 ring-2 ring-rose-200">
                                            {item.burstLimit || 0}
                                        </span>
                                    </td>
                                    <td className="p-4 text-center">
                                        <div className="flex justify-center gap-2">
                                            {can.viewFinancials && (
                                                <button 
                                                    onClick={() => { setSelectedItemForPriceLog(item); setShowPriceLogModal(true); }} 
                                                    className="p-2.5 hover:bg-purple-50 rounded-xl text-purple-600 transition-colors border border-transparent hover:border-purple-200"
                                                    title={t("settings.priceLogTooltip")}
                                                >
                                                    <DollarSign size={18}/>
                                                </button>
                                            )}
                                            <button 
                                                onClick={() => { setSelectedItem(item); setIsItemModalOpen(true); }} 
                                                className="p-2.5 hover:bg-blue-50 rounded-xl text-blue-600 transition-colors border border-transparent hover:border-blue-200"
                                                title={t("settings.editProductTooltip")}
                                            >
                                                <Edit2 size={18}/>
                                            </button>
                                            <button 
                                                onClick={() => requestConfirm(t("inventory.deleteProduct"), t("inventory.deleteProductConfirm"), () => db.deleteItem(item.id).then(fetchData))} 
                                                className="p-2.5 hover:bg-rose-50 rounded-xl text-rose-600 transition-colors border border-transparent hover:border-rose-200"
                                                title={t("settings.deleteProductTooltip")}
                                            >
                                                <Trash2 size={18}/>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Card List */}
                <div className="md:hidden divide-y divide-slate-100">
                    {filteredInventory.map(item => (
                        <div key={item.id} className="p-4">
                            <div className="flex items-start gap-3 mb-3">
                                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center overflow-hidden border border-slate-200 flex-shrink-0 cursor-pointer" onClick={() => item.image && setViewingImage(item.image)}>
                                    {item.image ? <img src={item.image} className="w-full h-full object-cover" alt=""/> : <Package size={18} className="text-slate-400"/>}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-slate-800 text-sm leading-tight">{item.name}</div>
                                    <div className="text-[11px] text-slate-400 font-mono mt-0.5">{item.sku}</div>
                                </div>
                                <span className={`px-2.5 py-1 rounded-lg text-xs font-bold flex-shrink-0 ${item.quantity <= item.minLevel ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                    {formatNumber(item.quantity)} {item.unit || 'pcs'}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Badge color="blue">{item.category}</Badge>
                                    {can.viewFinancials && <span className="text-xs font-bold text-slate-600">{formatCurrency(item.price)}</span>}
                                </div>
                                <div className="flex gap-1.5">
                                    {can.viewFinancials && <button onClick={() => { setSelectedItemForPriceLog(item); setShowPriceLogModal(true); }} className="p-2 hover:bg-purple-50 rounded-lg text-purple-600"><DollarSign size={16}/></button>}
                                    <button onClick={() => { setSelectedItem(item); setIsItemModalOpen(true); }} className="p-2 hover:bg-blue-50 rounded-lg text-blue-600"><Edit2 size={16}/></button>
                                    <button onClick={() => requestConfirm(t("inventory.deleteProduct"), t("inventory.deleteProductConfirm"), () => db.deleteItem(item.id).then(fetchData))} className="p-2 hover:bg-rose-50 rounded-lg text-rose-600"><Trash2 size={16}/></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {filteredInventory.length === 0 && (
                    <div className="p-12 text-center bg-slate-50">
                        <Package size={48} className="mx-auto mb-3 text-slate-300" />
                        <p className="text-slate-400 text-sm font-medium">{t("settings.noProductsFound")}</p>
                    </div>
                )}
            </Card>
        </div>
    );
});

SKUManagement.displayName = 'SKUManagement';

export default SKUManagement;
